import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";

/**
 * Modal de Aviso de Federação Não Filiada
 *
 * Exibido quando um usuário faz login em um portal de federação ao qual NÃO é
 * filiado e que também não possui grupos de permissão nesse portal (não é
 * administrador/colaborador). Oferece redirecioná-lo ao portal adequado.
 * Caso recuse, o usuário é deslogado automaticamente.
 *
 * É um modal bloqueante (sem botão de fechar).
 */
const FederacaoNaoFiliadaModal = () => {
  const { federacaoMismatch, navigateToPortal, logout } = useAuth();
  const [acao, setAcao] = useState(null); // "redirect" | "logout" | null

  if (!federacaoMismatch) return null;

  const { portalNome, destinoNome, redirectUrl } = federacaoMismatch;

  const handleIrParaDestino = async () => {
    if (!redirectUrl) return;
    setAcao("redirect");
    try {
      await navigateToPortal(redirectUrl);
    } catch (error) {
      console.error("[FederacaoNaoFiliadaModal] Erro ao redirecionar:", error?.message);
      setAcao(null);
    }
  };

  const handleSair = async () => {
    setAcao("logout");
    try {
      await logout();
    } catch (error) {
      console.error("[FederacaoNaoFiliadaModal] Erro ao deslogar:", error?.message);
      setAcao(null);
    }
  };

  const ocupado = acao !== null;

  return (
    <Modal
      show={true}
      backdrop="static"
      keyboard={false}
      centered
      aria-labelledby="federacao-nao-filiada-titulo"
      contentClassName="border-0 shadow-lg rounded-3 overflow-hidden"
    >
      {/* Cabeçalho gradiente — padrão dos modais de pesagem */}
      <Modal.Header
        style={{
          background: "linear-gradient(135deg, #1a237e 0%, #1565c0 100%)",
          border: 0,
          padding: "1rem 1.25rem",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <span className="material-icons-outlined text-white" style={{ fontSize: "2rem" }}>
            domain_disabled
          </span>
          <div>
            <h5 className="mb-0 fw-bold text-white lh-sm" id="federacao-nao-filiada-titulo">
              Portal não disponível para você
            </h5>
            <small className="text-white" style={{ opacity: 0.8 }}>
              Acesso restrito a filiados desta federação
            </small>
          </div>
        </div>
      </Modal.Header>

      {/* Corpo */}
      <Modal.Body className="p-0">
        {/* Bloco de informação principal */}
        <div
          className="d-flex gap-3 align-items-start p-4"
          style={{ background: "#f8f9fc", borderBottom: "1px solid #e9ecef" }}
        >
          <div
            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 56,
              height: 56,
              background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
              border: "1px solid #ffcc80",
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: "1.8rem", color: "#e65100" }}>
              warning_amber
            </span>
          </div>
          <div className="flex-grow-1">
            <div className="fw-semibold text-dark mb-1" style={{ fontSize: "0.95rem" }}>
              Você não é filiado a <strong>{portalNome}</strong>
            </div>
            <div className="text-muted" style={{ fontSize: "0.85rem" }}>
              Seu acesso a este portal é restrito a associados desta federação. Você será
              redirecionado para o portal correto ou poderá sair.
            </div>
          </div>
        </div>

        {/* Bloco de destino */}
        <div className="px-4 pt-3 pb-4">
          <small className="text-muted fw-semibold d-block mb-2">Para onde você será encaminhado</small>
          <div
            className="d-flex align-items-center gap-3 rounded-2 px-3 py-2"
            style={{ background: "#f0f4ff", border: "1px solid #dce4fb" }}
          >
            <span className="material-icons-outlined" style={{ color: "#1a237e", fontSize: "1.4rem" }}>
              home
            </span>
            <div>
              <div className="fw-semibold" style={{ fontSize: "0.9rem", color: "#1a237e" }}>
                {destinoNome}
              </div>
              {redirectUrl && (
                <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                  {redirectUrl}
                </div>
              )}
            </div>
          </div>

          <div
            className="d-flex align-items-center gap-2 mt-3 rounded-2 px-3 py-2"
            style={{ background: "#fff8e1", border: "1px solid #ffe082", fontSize: "0.8rem", color: "#795548" }}
          >
            <span className="material-icons-outlined" style={{ fontSize: "1rem" }}>info</span>
            Se optar por não continuar, você será desconectado deste portal automaticamente.
          </div>
        </div>
      </Modal.Body>

      {/* Rodapé */}
      <Modal.Footer
        className="d-flex justify-content-between"
        style={{ background: "#f8f9fc", borderTop: "1px solid #e9ecef", padding: "0.75rem 1.25rem" }}
      >
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleSair}
          disabled={ocupado}
          className="d-flex align-items-center gap-2"
        >
          {acao === "logout" ? (
            <>
              <Spinner as="span" animation="border" size="sm" />
              Saindo...
            </>
          ) : (
            <>
              <span className="material-icons-outlined" style={{ fontSize: "1rem" }}>logout</span>
              Sair
            </>
          )}
        </Button>

        {redirectUrl && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleIrParaDestino}
            disabled={ocupado}
            className="d-flex align-items-center gap-2"
            style={{ background: "linear-gradient(135deg, #1a237e 0%, #1565c0 100%)", border: 0 }}
          >
            {acao === "redirect" ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                Redirecionando...
              </>
            ) : (
              <>
                <span className="material-icons-outlined" style={{ fontSize: "1rem" }}>arrow_forward</span>
                Ir para {destinoNome}
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default FederacaoNaoFiliadaModal;
